import React, { useEffect, useRef, useState } from 'react';
import { AiAssistantPanel } from './AiAssistantPanel';
// Stable references so default props don't create new objects each render.
const EMPTY_MESSAGES = [];
const EMPTY_CONTEXT = {};
const createMessage = (role, content) => ({
    id: crypto.randomUUID(),
    role,
    content,
    createdAt: new Date().toISOString()
});
export const AiAssistant = ({ name = 'AI Assistant', context = EMPTY_CONTEXT, defaultOpen = false, status, onRetry, initialMessages = EMPTY_MESSAGES, client }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [sessionState, setSessionState] = useState({
        status: 'loading'
    });
    // `status` lets a caller (Storybook) pin the panel to a specific state
    // without going through a real `createSession` call.
    const effectiveStatus = status ?? sessionState.status;
    const sessionId = sessionState.status === 'ready' ? sessionState.sessionId : null;
    const [messages, setMessages] = useState(initialMessages);
    // Real API wiring (a follow-up PR) will make this a `useState` again.
    const isReplying = false;
    // Guards against a stale `createSession()` response overwriting a newer
    // one — e.g. rapid close-then-reopen, or retrying before the first call
    // resolves — by ignoring any response that isn't from the latest call.
    const latestRequestId = useRef(0);
    // Close on Escape while the panel is open.
    useEffect(() => {
        if (!isOpen)
            return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape')
                setIsOpen(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);
    // Reserve space for the panel by reflowing the page content to the left
    // while it's open (see the `body.ai-assistant-page-open` rule in the CSS).
    useEffect(() => {
        document.body.classList.toggle('ai-assistant-page-open', isOpen);
        return () => {
            document.body.classList.remove('ai-assistant-page-open');
        };
    }, [isOpen]);
    // Tells the CSS to shift HubSpot's help widget out of the AI Assistant button's way.
    // TODO: avoid targeting body directly — revisit once AiAssistantWrapper owns more of this.
    useEffect(() => {
        document.body.classList.add('ai-assistant-enabled');
        return () => {
            document.body.classList.remove('ai-assistant-enabled');
        };
    }, []);
    // Starts a brand-new session — nothing is persisted or rehydrated, so every
    // open (or retry) gets a fresh `sessionId`.
    const startSession = () => {
        const requestId = ++latestRequestId.current;
        setSessionState({ status: 'loading' });
        client
            .createSession()
            .then((response) => {
            if (latestRequestId.current !== requestId)
                return;
            setSessionState({ status: 'ready', sessionId: response.sessionId });
        })
            .catch((error) => {
            if (latestRequestId.current !== requestId)
                return;
            console.error(error);
            setSessionState({ status: 'error' });
        });
    };
    const handleOpen = () => {
        setIsOpen(true);
        // When `status` is passed, the caller owns the displayed state (Storybook
        // pinning a specific state), so skip the real session lifecycle.
        if (!status)
            startSession();
    };
    // Mirrors what clicking the Fab does — otherwise a panel that starts open
    // via `defaultOpen` would render the `ready` chat UI without ever having
    // called `client.createSession()`.
    useEffect(() => {
        if (defaultOpen)
            handleOpen();
        // Mount-only: `defaultOpen` is documented as an initial-render setting.
    }, []);
    const handleClose = () => {
        setIsOpen(false);
    };
    // The actual round-trip (calling the API, toggling isReplying, appending
    // the reply) is wired up in a follow-up PR.
    const handleSend = (text) => {
        setMessages((prev) => [...prev, createMessage('user', text)]);
    };
    // Real usage (no `status` pinned) always wires retry back to a fresh
    // session attempt unless the caller supplies its own `onRetry`.
    const effectiveOnRetry = onRetry ?? (status ? undefined : startSession);
    return (React.createElement(AiAssistantPanel, { context: context, isOpen: isOpen, isReplying: isReplying, messages: messages, name: name, onClose: handleClose, onOpen: handleOpen, onRetry: effectiveOnRetry, onSend: handleSend, sessionId: sessionId, status: effectiveStatus }));
};
